/**
 * Propagates pushed changes into the DOM.
 *
 * @internals app/components/push/domUpdates/DomManipulator
 */
import _ from 'lodash';
import DomPushAdapter from '../../push/domUpdates/DomPushAdapter';
import HandicapProvider from '../../HandicapProvider';
import jQuery from 'jquery';
import OddsFormatProvider from '../../OddsFormatProvider';
import SportsDataRepository from '../../SportsDataRepository';

/**
 * Logger.
 *
 * @type    {Function}
 *
 * @private
 */
var log = function () {
};

/**
 * Mapping of entity ID prefixes to types.
 *
 * @type    {Object}
 *
 * @private
 */
var types = {
    'OB_OU': 'selections',
    'OB_MA': 'markets',
    'OB_EV': 'events',
};

/**
 * Navigation callback function
 *
 * @type    {Function}
 *
 * @private
 */
var callback = null;

/**
 * Get the type of the node from the id.
 *
 * @param   {String} nodeId
 *
 * @returns {String}
 *
 * @privat;
 */
var getNodeType = function (nodeId) {

    if (typeof nodeId === 'undefined') {
        return '';
    }

    return (function () {

        var nodeType = nodeId.substr(0, 5);
        return types.hasOwnProperty(nodeType) ? types[nodeType] : '';
    })();
};

/**
 * Converts a boolean string to Boolean
 *
 * @todo docblock is not actually totally accurate due to return str
 *
 * @param   {String} str     string to convert
 *
 * @returns {mixed}
 *
 * @private
 */
var strToBoolean = function (str) {

    var values = {
        'false': false,
        'true': true,
    };

    if (values.hasOwnProperty(str)) {
        return values[str];
    }

    return str;
};

/**
 * Render a price change.
 *
 * @param   {Object} change     change values
 * @param   {Object} domNode   DOM Node
 *
 * @returns {void}
 *
 * @private
 */
var renderPrice = function (change, domNode) {

    var convertedPrice = OddsFormatProvider
        .convertSingle(change.node.currentprice);

    var components = change.node.currentprice.split('/');
    updatePriceAttributes(domNode, convertedPrice, components);
};

/**
 * Updates the attributes of the selection according to the pushed change in price
 *
 * @param {Object} domNode DOM Node representing a selection
 * @param {String} convertedPrice Formatted price (might be EVS for 1/1 for example)
 * @param {Array} components A pair composed by the numerator and denominator
 *
 * @returns {void}
 *
 * @private
 */
var updatePriceAttributes = function updatePriceAttributes(domNode, convertedPrice, components) {

    var priceElement = domNode.children('span');

    domNode.attr('data-num', components[0]);
    domNode.attr('data-denom', components[1]);
    domNode.attr('data-odds', convertedPrice);
    priceElement.attr('data-odds', convertedPrice);
    updatePriceElements(domNode, convertedPrice);
};

/**
 * 'BetButton' selection template has the price rendered in directly in the markup not via css using attributes
 *
 * @param {Object} domNode DOM Node representing a selection
 * @param {String} convertedPrice Formatted price (might be EVS for 1/1 for example)
 * @param {String} priceElement The dom element that renders the price
 *
 * @returns {void}
 *
 * @private
 */
var updatePriceElements = function updatePriceElements(domNode, convertedPrice) {

    if (domNode.hasClass('betbutton')) {

        domNode.find('.betbutton__odds').text(convertedPrice);
    }
};

/**
 * This function check that the dom node is not a SP selection
 * We don't update SP selections when the price is setup
 *
 * @param       domNode     DOM Node
 *
 * @returns     {boolean}
 *
 * @private
 */
var checkDomNodeIsSP = function (domNode) {

    return jQuery(domNode).data('odds') !== 'SP';
};

/**
 * Checks if price is changing
 *
 * @param   {Object} change     change values
 * @param   {Object} domNode   DOM Node
 *
 * @returns {Boolean}
 *
 * @private
 */
var priceIsChanging = function (change, domNode) {

    var numDenom = change.node.currentprice.split('/');
    var previous = getPreviousOdds(domNode);

    return (
        (parseInt(numDenom[0], 10) / parseInt(numDenom[1], 10)) !==
        (parseInt(previous[0], 10) / parseInt(previous[1], 10))
    );
};

/**
 * Get the current odds value from a dom node.
 *
 * @param   {Object} domNode   DOM Node
 *
 * @returns {Array}
 *
 * @private
 */
var getPreviousOdds = function (domNode) {

    var previousNum = domNode.attr('data-num');
    var previousDenom = domNode.attr('data-denom');

    return [previousNum, previousDenom];
};

/**
 * Toggles and entity visibility
 *
 * @param {Object} model The whole model object containing all the events, markets and selections
 * @param {Object} node The model entity we have to toggle the visibility for
 * @param {Object} nodeType The type of the node : event, market or selection
 * @param {Boolean} show or hide
 *
 * @returns {void}
 *
 * @private
 */
var toggleEntityVisibility = function toggleEntityVisibility(model, node, nodeType, show) {

    node.displayedOutModel = show;

    SportsDataRepository.setModel(model);

    //unlike a direct toggle on the current entity, in case of propagation the $dom is not set..
    SportsDataRepository.setDomElement(node, nodeType);

    if (node.$dom && !node.$dom.hasClass('noHide')) {

        node.$dom.toggleClass('displayNone', !show)
            .attr('data-displayed', show);

        updateNewSelectionVisibility(node, show);
    }
};

/**
 * Updates the visibility of the top container for the new selections layout to prevent names and handicaps
 * from being displayed when the selection drops off
 *
 * @param {Object} node jQuery dom Object
 * @param {Boolean} show Boolean indicating if the selection should be visible or hidden
 *
 * @returns {void}
 *
 * @private
 */
var updateNewSelectionVisibility = function updateNewSelectionVisibility(node, show) {

    var $parent;

    if (getNodeType(node.id) === 'selections') {

        $parent = node.$dom.parent();

        updateParentSelectionContainer($parent, show);

        $parent.toggleClass('displayNone', !show);
    }
};

/**
 * Updates the parent container visibility for a selection
 *
 * @param {Object} container Parent container
 * @param {Boolean} show Boolean indicating if the selection should be visible or hidden
 *
 * @returns {void}
 *
 * @private
 */
var updateParentSelectionContainer = function (container, show) {

    if (container.hasClass('btmarket__selection')) {

        container.toggleClass('displayNone', !show);
        container.parent('.btmarket__actions').next('hr').toggleClass('displayNone', !show);
    }
};

/**
 * Update Functions
 *
 * @type    {Object}
 *
 * @private
 */
var updateFunctions = {

    /**
     * BetInRun change handler for all pages
     *
     * @param  {Object} change   Change object
     *
     * @return {void}
     *
     * @private
     */
    updateBetInRun: function (change) {

        // notify the DOM change
        // At this moment we don't need that now so I'll this here, commented out, waiting for cases when
        // we would need to update any page regarding status changes.

        DomPushAdapter.push({
            id: change.node.id,
            attribute: change.attribute,
            value: strToBoolean(change.node.betInRun),
            nodeType: getNodeType(change.node.id),
        });
    },

    /**
     * Update a price.
     *
     * @param   {Object} change     change values
     * @param   {Object} domNode   DOM Node
     *
     * @returns {void}
     *
     * @private
     */
    // non camelcase is perfect here mate ;)
    updateCurrentprice: function (change, domNode) {

        if (checkDomNodeIsSP(domNode) && priceIsChanging(change, domNode)) {
            renderPrice(change, domNode);
        }
    },
    /**
     * Update status of entities.
     *
     * @param   {Object} change     change values
     * @param   {Object} domNode   DOM Node
     *
     * @returns {void}
     *
     * @private
     */
    updateStatus: function (change, domNode) {

        var disabledClasses = {
            markets: 'disabled-market',
            selections: 'disabled',
            events: 'disabled-event',
        };

        var nodeType = getNodeType(change.node.id);
        var active = (change.node.status === 'A');

        domNode
            .attr('data-status', change.node.status)
            .toggleClass(disabledClasses[nodeType], !active);

        DomPushAdapter.push({
            id: change.node.id,
            attribute: change.attribute,
            value: strToBoolean(change.node.status),
            nodeType: getNodeType(change.node.id),
        });
    },
    /**
     * Hide or show entities. Also handle the display propagation.
     *
     * @param   {Object} change     change values
     * @param   {Object} domNode   DOM Node
     *
     * @returns {void}
     *
     * @private
     */
    updateDisplayed: function (change) {

        var model = SportsDataRepository.getModel();

        /**
         * Set of functions to update the visibility of an entity.
         *
         * @type {Object}
         *
         * @private
         */
        var toggleFunctions = {

            entity: function (entityId, show) {

                var nodeType = getNodeType(entityId);

                var toggle = function () {

                    var node = model[nodeType][entityId];

                    if (node) {

                        toggleEntityVisibility(model, node, nodeType, show);
                    }

                    // notify the DOM change
                    DomPushAdapter.push({
                        id: entityId,
                        attribute: 'displayed',
                        value: show,
                        nodeType: nodeType,
                    });
                };

                if (model.hasOwnProperty(nodeType)) {

                    toggle();
                }
            },
        };

        /**
         * Set of functions to create a propagation effect after a node is updated.
         *
         * @type {Object}
         *
         * @private
         */
        var propUpFunctions = {

            // retrieves the fake display flag, made up to avoid changing the model when we need to hide stuff
            // If i.e we have all the selections displayed to false, the market must be displayed
            // false but without affecting the model. But if we dont mark it when it comes to
            // check if a event has displayed false markets we won't get them.
            // Also we need to leave the original model untouched as it can only be changed by push messages.

            /**
             * Gets the displayed value
             *
             * @param   {Object} obj
             *
             * @returns {Boolean}
             *
             * @private
             */
            getDisplayed: function (obj) {

                return strToBoolean((obj.hasOwnProperty('displayedOutModel')) ?
                    obj.displayedOutModel :
                    obj.displayed);
            },

            /**
             * Gets the displayed value for an OB object
             *
             * @param   {Object} obj
             *
             * @returns {Boolean}
             *
             * @private
             */
            getOBDisplayed: function (obj) {

                return strToBoolean(obj.displayed);
            },

            /**
             * Gets the markets displayed in an Event
             *
             * @param   {String} eventId id of the event
             *
             * @returns {Object}
             *
             * @private
             */
            displayedMarketsInEvent: function (eventId) {

                var markets = model.events[eventId].markets;

                if (model.events[eventId].alternateMarket) {
                    markets.push(model.events[eventId].alternateMarket.id);
                }

                var result = false;

                _.each(markets, function (marketId) {


                    var mrkt = model.markets[propUpFunctions.getId(marketId)];
                    var displayed = propUpFunctions.getDisplayed(mrkt) &&
                        mrkt.hasInPlay.toString() === model.events[eventId].isInPlay.toString();

                    if (mrkt.eventId === eventId && displayed === true) {
                        result = true;
                        return false;
                    }
                });

                return result;
            },

            /**
             * Gets ID from an object or just return in case it's not object
             *
             * @param {Object/String} elem
             *
             * @returns {String}
             *
             * @private
             */
            getId: function (elem) {

                if (_.isObject(elem)) {
                    return elem.id;
                } else {
                    return elem;
                }
            },

            /**
             * Gets the displayed selections for a given market
             *
             * @param   {String} marketId id of the market
             *
             * @returns {boolean}
             *
             * @private
             */
            displayedSelectionsInMarket: function (marketId) {

                var selections = model.markets[marketId].selections;
                var result = false;
                var market = model.markets[marketId];

                _.each(selections, function (selectionId) {

                    var sel = model.selections[propUpFunctions.getId(selectionId)];
                    var displayed = propUpFunctions.getDisplayed(sel);
                    if (sel.marketId === marketId &&
                        displayed === true &&
                        market.hasInPlay.toString() === model.events[market.eventId].isInPlay.toString()) {
                        result = true;
                        return false;
                    }
                });
                return result;
            },

            /**
             * Toggles an event associated with a market
             *
             * @param   {String} marketId
             *
             * @returns {void}
             *
             * @private
             */
            markets: function (marketId) {

                var eventId = model.markets[marketId].eventId;
                var eventEntity = model.events[eventId];
                var eventOPDisplayed = propUpFunctions.getOBDisplayed(eventEntity);

                if (eventOPDisplayed &&
                    !DomPushAdapter.preventPropagation(marketId)) {

                    toggleFunctions.entity(eventId, propUpFunctions.displayedMarketsInEvent(eventId));
                }
            },

            /**
             * Toggles a market associated with a selection
             *
             * @param   {String} selectionId
             *
             * @returns {void}
             *
             * @private
             */
            selections: function (selectionId) {

                var marketId = model.selections[selectionId].marketId;
                var market = model.markets[marketId];
                var marketOPDisplayed = propUpFunctions.getOBDisplayed(market);

                // if the market was originally displayed false from openbet it should never get displayed true
                // by a selection update
                if (marketOPDisplayed) {
                    toggleFunctions.entity(marketId, propUpFunctions.displayedSelectionsInMarket(marketId));
                    propUpFunctions.markets(marketId);
                }
            },
        };

        /**
         * Check for upwards propagation in case we get a display false
         *
         * @params  {String} nodeType
         *
         * @return {void}
         *
         * @private
         */
        var checkUpPropagation = function (nodeType) {

            if (propUpFunctions.hasOwnProperty(nodeType) &&
                !DomPushAdapter.preventPropagation(change.node.id)) {

                propUpFunctions[nodeType](change.node.id);
            }
        };

        /**
         * Update attribute and toggle. After that check upwards propagation
         *
         * @return {void}
         *
         * @private
         */
        var update = function () {

            var displayed = strToBoolean(change.node.displayed);
            var nodeType = getNodeType(change.node.id);

            /**
             * Check children before displaying.
             *
             * @type    {Object}
             *
             * @private
             */
            var updateByType = {

                /**
                 * basic case, rising up the propagation. Selections are a good candidate,
                 * also it will be used for events since we are making it work just for events page.
                 *
                 * returns {void}
                 *
                 * @private
                 */
                genericUpdate: function () {

                    toggleFunctions.entity(change.node.id, displayed);
                    checkUpPropagation(nodeType);
                },

                /**
                 * Updates a given market
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                updateMarket: function () {

                    var displayedSelections = propUpFunctions.displayedSelectionsInMarket(change.node.id);

                    if (displayedSelections) {
                        // This happens at events page
                        //toggleFunctions.entity(change.node.id,displayed);
                        updateByType.genericUpdate();
                    }
                },

                /**
                 * Updates a given event
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                updateEvent: function () {

                    var displayedMarkets = propUpFunctions.displayedMarketsInEvent(change.node.id);

                    if (displayedMarkets) {
                        updateByType.genericUpdate();
                    }
                },
            };

            /**
             * Manages the visibility
             *
             * @returns {void}
             *
             * @private
             */
            var manageDisplay = function () {

                var displayFunctions = {
                    'markets': updateByType.updateMarket,
                    'selections': updateByType.genericUpdate,
                    'events': updateByType.updateEvent,
                };

                displayFunctions[nodeType]();
            };

            manageDisplay();
        };

        update();
    },

    /**
     * Update handicap value.
     *
     * @param {Object} change The change values
     *
     * @returns {void}
     *
     * @private
     */
    updateHcapValue: function (change) {

        var model = SportsDataRepository.getModel();
        var market = model.markets[change.node.id];

        HandicapProvider.updateHandicap(market, change.node.hcapValue);
    },

    /**
     * Function updates domNode startDateTime values and triggers a push message to subscribers
     *
     * @param {Object} change Changed values
     * @param {Object} domNode jQuery DOM element
     *
     * @returns {void}
     *
     * @private
     */
    updateStartDateTime: function (change, domNode) {

        domNode.attr('data-startdatetime', change.node.startDateTime);

        DomPushAdapter.push({
            id: change.node.id,
            attribute: change.attribute,
            value: change.node.startDateTime,
            nodeType: getNodeType(change.node.id),
        });
    },
};

/**
 * Determine whether an attribute has changed.
 *
 * @param   {Object} change
 * @param   {Object} domNode
 *
 * @returns {Boolean}
 *
 * @private
 */
var hasAttributeChanged = function (change, domNode) {

    var chkFunctions = {
        processCurrentPrice: function () {

            var previousNum = domNode.attr('data-num');
            var previousDenom = domNode.attr('data-denom');
            var currentNum = change.node.currentPriceNum;
            var currentDenom = change.node.currentPriceDen;

            // Don't check displayed price as it may have been formatted. Compare the raw price.
            var hasChanged = (previousNum !== currentNum) || (previousDenom !== currentDenom);

            var previousPrice = domNode.children('span').eq(0).attr('data-odds');

            // Notification to the DomPushAdapter in case there is a callback
            // delegated to handle the price changes by a Page
            DomPushAdapter.push({
                id: change.node.id,
                attribute: change.attribute, //"currentprice"
                value: change.node.currentprice,
                nodeType: getNodeType(change.node.id),
                previousPrice: previousPrice,
                hasChanged: hasChanged,
            });

            return hasChanged;
        },
        processStatus: function () {

            var domAttrVal = domNode.attr('data-status');
            var domValue = (domAttrVal === 'A') ? 'A' : domAttrVal;

            return (change.node.status !== domValue);
        },
        processBetInRun: function () {

            return change.node.betInRun.toString() !== domNode.attr('data-betinrun');
        },
        processDisplayed: function () {

            return change.node.displayed !== domNode.attr('data-displayed');
        },
        processHandicap: function () {

            return change.node.hcapValue !== domNode.attr('data-hcapValue');
        },
        processStartDateTime: function () {

            //PDS: 2015-10-20T12:40:00.000+0000; API: 2015-10-20T12:40:00+00:00
            //A fix without using moment library, which is not so fast
            change.node.startDateTime = change.node.startDateTime.replace('.000+0000', '+00:00');
            return change.node.startDateTime !== domNode.attr('data-startdatetime');
        },
    };

    var attributes = {
        'currentprice': chkFunctions.processCurrentPrice,
        'status': chkFunctions.processStatus,
        'betInRun': chkFunctions.processBetInRun,
        'displayed': chkFunctions.processDisplayed,
        'hcapValue': chkFunctions.processHandicap,
        'startDateTime': chkFunctions.processStartDateTime,
    };

    if (attributes.hasOwnProperty(change.attribute)) {
        return attributes[change.attribute]();
    } else {
        return false;
    }
};

/**
 * Update an attribute.
 *
 * @param   {Object} change
 * @param   {Object} domNode
 *
 * @returns {void}
 *
 * @private
 */
var updateAttribute = function (change, domNode) {

    var fName = 'update' + change.attribute.charAt(0)
        .toUpperCase() + change.attribute.slice(1);
    if (updateFunctions.hasOwnProperty(fName)) {
        updateFunctions[fName](change, domNode);
    }

    return;
};

/**
 * Propagate a change to the DOM.
 *
 * @param   {Object} change
 *
 * @returns {Boolean}
 *
 * @private
 */
var updateDom = function (change) {

    var domNode = change.node.$dom;

    log('DomManipulator.updateDom()');

    if (!hasAttributeChanged(change, domNode)) {
        return false;
    }

    updateAttribute(change, domNode);
    return true;
};

/**
 * Update some selections.
 *
 * @param   {Array} changesList
 *
 * @returns {void}
 *
 * @public
 */
var updates = function (changesList) {

    log('DomManipulator.updateSelections()');

    if (changesList) {

        changesList.forEach(updateDom);
    } else {

        log('Nothing to be updated');
    }

};

/**
 * Public functions.
 *
 * @type    {Object}
 *
 * @public
 */
var api = {

    /**
     * Updates the data based on a changes object
     *
     * @param   {Object} changes    changes object to update
     *
     * @returns {void}
     *
     * @public
     */
    update: function (changes) {

        log('DomManipulator.update()');
        log('Changes ----');
        log(changes);
        updates(changes.selections);
        updates(changes.events);
        updates(changes.markets);
    },

    /**
     * Handles the push of a new node by delegating to the DomPushAdapter component. This aims to comply with the
     * single responsibility principle
     *
     * @param {Object} info object containing the node type and properties
     *
     * @returns {void}
     *
     * @private
     */
    newNode: function (node, nodeType, initialTopic, topic) {
        DomPushAdapter.notifyNewNode({
            lazy: true,
            id: node.id,
            attribute: node.attribute,
            value: node.value,
            nodeType: nodeType,
            entity: SportsDataRepository.getEntityFromQueue(node.id),
            topics: {
                parent: initialTopic,
                current: topic,
            },
        });
    },

    /**
     * Sets the callback
     *
     * @param   {Function} f
     *
     * @returns {void}
     *
     * @public
     */
    setCallback: function (f) {

        callback = f;
    },
};

export default api;
